import { observer } from "mobx-react-lite";
import { useFormikContext } from "formik";
import {
  ArrowLeftRight,
  Building2,
  LayoutDashboard,
  ListFilter,
  Network,
  RefreshCw,
} from "lucide-react";
import { createElement, useState } from "react";

import { useTran } from "@/hooks";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/features/auth";
import { ConfirmDialog } from "@/components/common";
import CompanyFeatureSettingsFields from "./CompanyFeatureSettingsFields";

const TARGET_FREQUENCY_OPTIONS = [
  { value: "monthly", labelKey: "common.monthly" },
  { value: "yearly", labelKey: "common.yearly" },
];

const SYSTEM_SETTING_OPTIONS = [
  {
    key: "dashboardVariant",
    titleKey: "benchmark_dashboard",
    descriptionKey: "benchmark_dashboard_desc",
    Icon: LayoutDashboard,
    systemAdminOnly: true,
  },
  {
    key: "allowAssetList",
    titleKey: "Allow Asset List",
    descriptionKey: "Allow this company to access Asset Lists.",
    Icon: Building2,
    systemAdminOnly: true,
  },
  {
    key: "enableMonthlyYearlySwitch",
    titleKey: "allow_monthly_yearly_switch",
    descriptionKey: "allow_monthly_yearly_switch_desc",
    Icon: ArrowLeftRight,
  },
  {
    key: "enableViewDetailsSwitch",
    titleKey: "auto_switch_target_overview",
    descriptionKey: "auto_switch_target_overview_desc",
    Icon: RefreshCw,
  },
  {
    key: "useJJOrganizationSelector",
    titleKey: "use_jj_org_selector",
    descriptionKey: "use_jj_org_selector_desc",
    Icon: Network,
  },
  {
    key: "showOnlyMetricsWithTargetAndData",
    titleKey: "show_metrics_with_target_data",
    descriptionKey: "show_metrics_with_target_data_desc",
    Icon: ListFilter,
  },
];

const renderSystemSettingIcon = (Icon) =>
  createElement(Icon, {
    className: "h-4 w-4",
    "aria-hidden": true,
  });

const CompanySetupSystemSettings = observer(() => {
  const { t } = useTran();
  const { user } = useAuthStore();
  const { values, setFieldValue, setFieldTouched } = useFormikContext();
  const [pendingFrequency, setPendingFrequency] = useState(null);

  // Gate behind a confirm: the company default only seeds NEW KPI targets,
  // so switching it is easy to misread as retroactive.
  const handleTargetFrequencyChange = (value) => {
    if (value === (values.targetFrequency || "yearly")) return;
    setPendingFrequency(value);
  };

  const confirmTargetFrequencyChange = () => {
    if (!pendingFrequency) return;
    setFieldValue("targetFrequency", pendingFrequency, true);
    setFieldTouched("targetFrequency", true, false);
    setPendingFrequency(null);
  };

  const handleSystemSettingChange = (key, checked) => {
    const value =
      key === "dashboardVariant"
        ? checked
          ? "benchmark"
          : "standard"
        : Boolean(checked);
    setFieldValue(key, value, true);
    setFieldTouched(key, true, false);
  };

  const pendingFrequencyLabel =
    pendingFrequency === "monthly" ? t("common.monthly") : t("common.yearly");

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div>
          <p className="text-sm font-medium text-foreground">
            {t("target_setup_frequency")}
          </p>
          <p className="text-xs text-muted-foreground">
            {t("target_frequency_desc")}
          </p>
        </div>
        <div className="grid w-full grid-cols-2 gap-1 rounded-lg border border-emerald-200 bg-emerald-50/60 p-1">
          {TARGET_FREQUENCY_OPTIONS.map((option) => {
            const isActive =
              (values.targetFrequency || "yearly") === option.value;

            return (
              <button
                key={option.value}
                type="button"
                onClick={() => handleTargetFrequencyChange(option.value)}
                className={cn(
                  "h-8 rounded-md text-xs font-semibold transition-all",
                  isActive
                    ? "bg-nxstart-green text-white shadow-sm"
                    : "text-emerald-700 hover:bg-white/70",
                )}
              >
                {t(option.labelKey)}
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-lg border border-emerald-200 bg-emerald-50/30 p-3">
        <div className="mb-3">
          <p className="text-sm font-semibold text-foreground">
            {t("system_settings")}{" "}
            <span className="font-normal text-muted-foreground">
              ({t("common.optional_badge", { defaultValue: t("optional") })})
            </span>
          </p>
          <p className="text-xs text-muted-foreground">
            {t("system_settings_desc")}
          </p>
        </div>

        <div className="space-y-3">
          {SYSTEM_SETTING_OPTIONS.filter(
            ({ systemAdminOnly }) =>
              !systemAdminOnly || user?.role === "system_admin",
          ).map(({ key, titleKey, descriptionKey, Icon }) => (
            <div key={key} className="flex items-center gap-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-emerald-100 text-emerald-700">
                {renderSystemSettingIcon(Icon)}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-foreground">
                  {t(titleKey)}
                </p>
                <p className="text-[11px] leading-4 text-muted-foreground">
                  {t(descriptionKey)}
                </p>
              </div>
              <Switch
                checked={
                  key === "dashboardVariant"
                    ? values.dashboardVariant === "benchmark"
                    : values[key] !== false
                }
                onCheckedChange={(checked) =>
                  handleSystemSettingChange(key, checked)
                }
                className="shrink-0 data-[state=checked]:bg-nxstart-green"
                aria-label={t(titleKey)}
              />
            </div>
          ))}
          <CompanyFeatureSettingsFields />
        </div>
      </div>

      <ConfirmDialog
        open={Boolean(pendingFrequency)}
        onOpenChange={(open) => {
          if (!open) setPendingFrequency(null);
        }}
        title={t("common.confirm_target_frequency_change_title", {
          frequency: pendingFrequencyLabel,
        })}
        description={t("common.confirm_target_frequency_change_desc")}
        confirmLabel={t("common.confirm_target_frequency_change_action", {
          frequency: pendingFrequencyLabel,
        })}
        cancelLabel={t("common.cancel", { defaultValue: t("Cancel") })}
        onConfirm={confirmTargetFrequencyChange}
      />
    </div>
  );
});

export default CompanySetupSystemSettings;
